// src/utils/sentenceClozeHelpers.js

// grobe, aber brauchbare Liste
const SPANISH_ARTICLES = new Set([
  "el",
  "la",
  "los",
  "las",
  "un",
  "una",
  "unos",
  "unas",
]);
const SPANISH_KEY_PREPOSITIONS = new Set([
  "a",
  "en",
  "de",
  "con",
  "para",
  "por",
  "desde",
  "hasta",
  "sobre",
  "sin",
]);
// Davor steht ein Substantiv, kein Verb ("mi casa", "la cena")
const SPANISH_NOUN_BEFORE = new Set([
  ...SPANISH_ARTICLES,
  ..."del al mi mis tu tus su sus nuestro nuestra nuestros nuestras cada otro otra otros otras este esta ese esa mucho mucha poco poca".split(" "),
]);

// Französisch: Artikel samt Kontraktionen (l' bekommt nie eine Lücke, siehe isGluedToken)
const FRENCH_ARTICLES = new Set("le la les l' un une des du au aux".split(" "));
const FRENCH_KEY_PREPOSITIONS = new Set(
  "à de d' en dans avec pour par sur sans chez sous vers entre depuis pendant".split(" ")
);
const FRENCH_NOUN_BEFORE = new Set([
  ...FRENCH_ARTICLES,
  ..."de d' mon ma mes ton ta tes son sa ses notre nos votre vos leur leurs ce cet cette ces chaque quelques plusieurs".split(" "),
]);

// Davor sind le/la/les/l' Objektpronomen, kein Artikel ("je la porte", "il ne l'aide pas");
// weitere Pronomen dazwischen werden übersprungen ("je te l'explique")
const FRENCH_BEFORE_OBJECT_PRONOUN = new Set("je j' tu il elle on nous vous ils elles ne n'".split(" "));
const FRENCH_CLITICS = new Set("me m' te t' se s' nous vous lui leur y en".split(" "));

/** Ist bases[idx - 1] (le/la/les/l'/leur) ein Objektpronomen vor dem Verb bases[idx]? */
function isFrenchObjectPronoun(bases, idx) {
  if (!["le", "la", "les", "l'", "leur"].includes(bases[idx - 1])) return false;
  let k = idx - 2;
  while (k >= 0 && FRENCH_CLITICS.has(bases[k]) && !FRENCH_BEFORE_OBJECT_PRONOUN.has(bases[k])) k--;
  return FRENCH_BEFORE_OBJECT_PRONOUN.has(bases[k]) || (k >= 0 && FRENCH_CLITICS.has(bases[k]));
}

const ROLE_WORDS = {
  es: { articles: SPANISH_ARTICLES, prepositions: SPANISH_KEY_PREPOSITIONS, nounBefore: SPANISH_NOUN_BEFORE },
  fr: { articles: FRENCH_ARTICLES, prepositions: FRENCH_KEY_PREPOSITIONS, nounBefore: FRENCH_NOUN_BEFORE },
};

const PUNCT_REGEX = /^[.,;:!?¡¿«»"“”…]+|[.,;:!?¡¿«»"“”…]+$/g;

/**
 * Normalisiert ein Token: Kleinbuchstaben, ’ → ', Satzzeichen am Rand weg,
 * Bindestrich angehängter französischer Pronomen ("-toi", "-t-il") vorne weg
 */
export function normalizeToken(raw) {
  return raw
    .toLowerCase()
    .replace(/’/g, "'")
    .replace(PUNCT_REGEX, "")
    .replace(/^-(t-)?/, "");
}

// Französisch: verkürzte Wörter vor Vokal (j'ai, l'eau, qu'il, jusqu'à), wie in wordForms.js
const FRENCH_ELISION_PREFIX = /^((?:l|d|j|m|t|s|c|n|qu|jusqu|lorsqu|puisqu|quoiqu)')(.+)$/i;
// angehängte Pronomen: viens-tu, lève-toi, dis-le-moi, a-t-il, est-ce
const FRENCH_PRONOUN_SUFFIX =
  /^(.+?)((?:-t)?-(?:moi|toi|lui|nous|vous|le|la|les|leur|y|en|je|tu|il|elle|on|ils|elles|ce))$/i;
// Wörter mit Bindestrich, die keine angehängten Pronomen haben
const FRENCH_HYPHEN_WORDS = new Set(["rendez-vous", "vis-à-vis"]);
const LEAD_PUNCT = /^[«"“(¿¡]+/;
const TRAIL_PUNCT = /[.,;:!?»"”)…]+$/;

/** Einzelne Teile eines französischen Worts: "J'ai" → ["J'", "ai"], "Viens-tu?" → ["Viens", "-tu?"] */
function splitFrenchWord(word) {
  const lead = (word.match(LEAD_PUNCT) || [""])[0];
  const rest = word.slice(lead.length);
  const trail = (rest.match(TRAIL_PUNCT) || [""])[0];
  let core = rest.slice(0, rest.length - trail.length);
  if (!core) return [word];

  const prefixes = [];
  let m;
  while ((m = core.match(FRENCH_ELISION_PREFIX))) {
    prefixes.push(m[1]);
    core = m[2];
  }
  const suffixes = [];
  if (!FRENCH_HYPHEN_WORDS.has(core.toLowerCase())) {
    while ((m = core.match(FRENCH_PRONOUN_SUFFIX))) {
      suffixes.unshift(m[2]);
      core = m[1];
    }
  }
  const pieces = [...prefixes, core, ...suffixes];
  pieces[0] = lead + pieces[0];
  pieces[pieces.length - 1] += trail;
  return pieces;
}

/**
 * Tokenisierung: nach Leerzeichen splitten, leere Tokens filtern.
 * Französisch (`lang` = "fr") zusätzlich: ’ → ', verkürzte Wörter und angehängte Pronomen als
 * eigene Teile (j'ai → j' + ai, viens-tu → viens + -tu), einzelne Satzzeichen („ ?“, „« “)
 * hängen am Nachbarwort. Teile ohne Leerzeichen dazwischen erkennt isGluedToken.
 */
export function tokenizeTarget(target, lang) {
  const words = target
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  if (lang !== "fr") return words;

  const tokens = [];
  let pendingLead = "";
  for (const raw of words) {
    const word = raw.replace(/’/g, "'");
    if (!normalizeToken(word)) {
      // reines Satzzeichen: öffnende ans nächste Wort, sonst ans vorige
      if (LEAD_PUNCT.test(word) && !TRAIL_PUNCT.test(word)) pendingLead += word;
      else if (tokens.length > 0) tokens[tokens.length - 1] += word;
      else pendingLead += word;
      continue;
    }
    const pieces = splitFrenchWord(pendingLead + word);
    pendingLead = "";
    tokens.push(...pieces);
  }
  if (pendingLead && tokens.length > 0) tokens[tokens.length - 1] += pendingLead;
  return tokens;
}

/**
 * Teil eines Worts ohne eigenes Leerzeichen (nur Französisch): endet auf Apostroph (j') oder
 * hängt mit Bindestrich am vorigen Teil (-tu). Bekommt keine Lücke.
 */
export function isGluedToken(token) {
  const core = (token || "").replace(LEAD_PUNCT, "").replace(TRAIL_PUNCT, "");
  return core.endsWith("'") || core.startsWith("-");
}

/** Steht zwischen Token `idx - 1` und `idx` ein Leerzeichen? */
export function joinsPrevious(tokens, idx) {
  if (idx === 0) return false;
  const prev = (tokens[idx - 1] || "").replace(TRAIL_PUNCT, "");
  return prev.endsWith("'") || (tokens[idx] || "").replace(LEAD_PUNCT, "").startsWith("-");
}

/**
 * Baut ein Set bekannter Verbformen aus den geladenen spanischen Verben.
 * Erwartet Array aus loadVerbFormsForCloze (src/data/loadConjugations.js).
 *
 * Beispiel-Objekt:
 * {
 *   id: "es-v-0001",
 *   infinitive: "ser",
 *   "1s": "soy",
 *   "2s": "eres",
 *   ...
 * }
 */
export function buildSpanishVerbFormsSet(spanishVerbs) {
  const forms = new Set();
  // Formen, die auch Substantiv/Adjektiv sind ("casa", "frío"), siehe analyzeTokens
  forms.ambiguous = new Set();
  for (const v of spanishVerbs) {
    for (const form of v.ambiguous || []) forms.ambiguous.add(normalizeToken(form));
    // nur Präsensformen (otherForms zählen hier bewusst nicht als Verb)
    for (const key of Object.keys(v)) {
      if (key === "id" || key === "infinitive") continue;
      const value = v[key];
      if (!value || typeof value !== "string") continue;
      forms.add(normalizeToken(value));
    }
    // Infinitiv selbst auch
    if (v.infinitive) {
      forms.add(normalizeToken(v.infinitive));
    }
  }
  return forms;
}

/**
 * Analysiert Tokens grob nach "Rolle".
 * Gibt ein Array gleicher Länge zurück mit Labels:
 * - "verb"
 * - "article"
 * - "prep"
 * - "content"
 * - "other"
 */
export function analyzeTokens(tokens, verbFormsSet, lang) {
  const { articles, prepositions, nounBefore } = ROLE_WORDS[lang] || ROLE_WORDS.es;
  const ambiguous = verbFormsSet?.ambiguous;
  const bases = tokens.map(normalizeToken);
  const roles = bases.map((base) => {
    if (!base) return "other";

    // Artikel/Präpositionen zuerst: Verbformen wie "para" (parar) sollen sie nicht überdecken
    if (articles.has(base)) return "article";
    if (prepositions.has(base)) return "prep";
    if (verbFormsSet && verbFormsSet.has(base) && !ambiguous?.has(base)) return "verb";

    if (base.length >= 5) return "content";

    return "other";
  });

  // Französisch: "est", "porte", "fait" sind Verb, außer ein Begleiter steht davor
  // ("la porte", "l'est", "le fait"); le/la/les/l' nach Subjektpronomen oder "ne" sind
  // Objektpronomen ("il la porte")
  if (lang === "fr") {
    if (ambiguous) {
      bases.forEach((base, idx) => {
        if (!ambiguous.has(base) || roles[idx] === "article" || roles[idx] === "prep") return;
        if (!nounBefore.has(bases[idx - 1]) || isFrenchObjectPronoun(bases, idx)) roles[idx] = "verb";
      });
    }
    return roles;
  }

  // "casa", "frío", "trabajo": Verb nur, wenn der Satz sonst keins hat und kein Begleiter davorsteht
  // ("Trabajo mucho" ja, "Mi trabajo es difícil" / "Hace frío" nein), wie im Prüfskript cloze-tenses.js
  if (ambiguous && !roles.includes("verb")) {
    bases.forEach((base, idx) => {
      if (ambiguous.has(base) && !nounBefore.has(bases[idx - 1])) roles[idx] = "verb";
    });
  }
  return roles;
}

/**
 * Wählt Indizes, die zu Lücken werden sollen (Option B).
 * Heuristik:
 * - alle Verben immer Lücke (wenn nicht extrem viele)
 * - ggf. 1–2 zusätzliche Wörter (Artikel/Präposition/Content),
 *   damit 2–5 Lücken insgesamt entstehen.
 */
export function selectGapIndices(tokens, roles, options = {}) {
  const minGaps = options.minGaps ?? 2;
  const maxGaps = options.maxGaps ?? 5;

  const verbIndices = [];
  const extraCandidates = [];

  roles.forEach((role, idx) => {
    if (role === "verb") {
      verbIndices.push(idx);
    } else if (role === "article" || role === "prep" || role === "content") {
      extraCandidates.push(idx);
    }
  });

  let gapIndices = [...verbIndices];

  // Wenn gar keine Verben erkannt wurden, nimm Content-Wörter
  if (gapIndices.length === 0 && extraCandidates.length > 0) {
    gapIndices.push(extraCandidates[0]);
  }

  // Shuffle helper
  function shuffle(arr) {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  // Falls zu viele Verb-Lücken → runterkappen
  if (gapIndices.length > maxGaps) {
    gapIndices = shuffle(gapIndices).slice(0, maxGaps);
  }

  // Wenn wir noch Lücken "frei" haben, fülle mit Extras auf
  const remainingSlots = maxGaps - gapIndices.length;
  if (remainingSlots > 0 && extraCandidates.length > 0) {
    const extras = shuffle(extraCandidates).filter(
      (idx) => !gapIndices.includes(idx)
    );
    gapIndices = gapIndices.concat(extras.slice(0, remainingSlots));
  }

  // Sicherstellen, dass wir mindestens minGaps haben (sofern möglich)
  if (gapIndices.length < minGaps) {
    const nonGapIndices = tokens
      .map((_, idx) => idx)
      .filter((idx) => !gapIndices.includes(idx));
    if (nonGapIndices.length > 0) {
      gapIndices.push(nonGapIndices[0]);
    }
  }

  // Sortieren für einfachere Darstellung
  gapIndices.sort((a, b) => a - b);
  return gapIndices;
}
